/**
 * useBids — list saved bids for the owner/admin bidding tool.
 *
 * Scope is enforced by RLS (owner/admin, own org only). Returns active
 * bids newest-first. The bidding tool is the only surface that reads this
 * table; cleaners/supervisors/clients have no RLS policy on `bids`.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useSupabase }                    from './useSupabase'
import type { BidInputs }                 from '@/lib/bidding/engine'

export type BidStatus = 'draft' | 'sent' | 'won' | 'lost'

export const BID_STATUSES: readonly BidStatus[] = ['draft', 'sent', 'won', 'lost'] as const

export interface BidRow {
  id:                  string
  organization_id:     string
  created_by_user_id:  string | null
  title:               string
  client_name:         string | null
  job_type:            string
  status:              BidStatus
  inputs_json:         BidInputs
  unit_count:          number
  total_labor_minutes: number
  total_cost_cents:    number
  total_bid_cents:     number
  notes:               string | null
  is_active:           boolean
  created_at:          string
  updated_at:          string
}

export const BID_COLUMNS =
  'id, organization_id, created_by_user_id, title, client_name, job_type, status,' +
  ' inputs_json, unit_count, total_labor_minutes, total_cost_cents, total_bid_cents,' +
  ' notes, is_active, created_at, updated_at'

export function useBids(): UseQueryResult<BidRow[]> {
  const supabase = useSupabase()

  return useQuery({
    queryKey: ['bids'],
    queryFn: async (): Promise<BidRow[]> => {
      const { data, error } = await supabase
        .from('bids')
        .select(BID_COLUMNS)
        .eq('is_active', true)
        .order('created_at', { ascending: false })

      if (error) throw error
      return (data ?? []) as unknown as BidRow[]
    },
  })
}
